import { createAction } from '@reduxjs/toolkit';
import { Role } from '../../mappers/business/toBusinessModel';
import { EventSource } from '../../utils/analyticsService';
import { ymTrackSubscribe } from '../../utils/metrika';
import { SharingMethod } from '../../utils/share';
import { showNotificationAsync } from '../notifications/actions';
import { selectPostById } from '../posts/selectors';
import { ReportReasons } from '../posts/types';
import { setUserRecords } from '../user/actions';
import { CURRENT_USER_ID } from '../user/constants';
import { selectUserById } from '../user/selectors';
import { BusinessCollection } from './constants';
import * as businessService from './service';
import type { BusinessDataModel, BusinessModel } from './types';
import type { RootState } from '../store';
import type { AnyAction } from 'redux';
import type { ThunkAction } from 'redux-thunk';

export const setBusinessRecords = createAction(
    '@@business/SET_USER_RECORDS',
    (...payload: BusinessDataModel[]) => ({ payload }),
);

export const setBusinessCollection = createAction<{
    namespace: string;
    identifiers: Array<BusinessDataModel['id']>;
}>('@@business/SET_BUSINESS_COLLECTION');

export const setBusinessCollectionLoading = createAction<{
    namespace: string;
    isLoading: boolean;
}>('@@business/SET_BUSINESS_COLLECTION_LOADING');

export const setBusinessCollectionFilters = createAction<{
    namespace: string;
    filters: Record<string, any>;
}>('@@business/SET_BUSINESS_COLLECTION_FILTERS');

export const removeBusiness = createAction<string>('@@business/REMOVE_BUSINESS');

export function removeBusinessAsync(
    business?: BusinessModel,
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        const currentProfile = selectUserById(getState(), CURRENT_USER_ID);
        await businessService.removeBusiness(business, currentProfile);

        if (business) {
            dispatch(removeBusiness(business.id));
        }

        if (
            currentProfile &&
            currentProfile.profile?.id === business?.id &&
            currentProfile.role !== Role.SUPER_ADMIN
        ) {
            dispatch(
                setUserRecords({
                    ...currentProfile,
                    profile: undefined,
                }),
            );
        }
    };
}

export function loadBusinessByIdAsync(
    businessId: string,
): ThunkAction<Promise<BusinessModel | undefined>, RootState, undefined, AnyAction> {
    return async (dispatch) => {
        try {
            const business = await businessService.loadBusinessById(businessId);

            dispatch(setBusinessRecords(business));

            return business;
        } catch (error) {
            dispatch(showNotificationAsync('Не удалось загрузить данные'));
        }

        return undefined;
    };
}

export function loadBusinessCollectionAsync(
    collection: BusinessCollection,
    params: Record<string, any> = {},
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch) => {
        dispatch(setBusinessCollectionLoading({ namespace: collection, isLoading: true }));
        dispatch(setBusinessCollectionFilters({ namespace: collection, filters: params }));

        if (!params.next) {
            dispatch(setBusinessCollection({ namespace: collection, identifiers: [] }));
        }

        try {
            const response = await businessService.loadBusinessCollection(collection, params);

            dispatch(setBusinessRecords(...response.records.business));
            dispatch(
                setBusinessCollection({ namespace: collection, identifiers: response.business }),
            );
        } catch (error) {
            dispatch(showNotificationAsync('Не удалось загрузить данные'));
        }

        dispatch(
            setBusinessCollectionLoading({
                namespace: collection,
                isLoading: false,
            }),
        );
    };
}

export const setBusinessSubscription = createAction<{
    subscribed: boolean;
    businessId: BusinessDataModel['id'];
}>('@@business/SET_USER_SUBSCRIPTION');

export const addReportedBusiness = createAction<string>('@@business/ADD_REPORTED_BUSINESS');

export function subscribeToBusinessAsync(
    business: BusinessModel,
    eventSource: EventSource,
    postNumber?: number,
    postId?: string,
    collectionId?: string,
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        dispatch(
            setBusinessSubscription({
                businessId: business.publicId,
                subscribed: !business.subscribed,
            }),
        );

        try {
            await businessService.subscribeToBusiness(business, {
                eventSource,
                postNumber,
                postId,
                collectionId,
            });
            const currentProfile = selectUserById(getState(), CURRENT_USER_ID);

            dispatch(
                showNotificationAsync(
                    business.subscribed
                        ? `Вы отписались от ${business.displayName}`
                        : `Вы подписались на ${business.displayName}`,
                ),
            );

            if (currentProfile) {
                dispatch(
                    setUserRecords({
                        ...currentProfile,
                        subscriptions:
                            currentProfile.subscriptions + (business.subscribed ? -1 : 1),
                    }),
                );
            }

            const postModel = selectPostById(getState(), postId);

            if (!business.subscribed) {
                ymTrackSubscribe({ own: postModel?.ownersData?.isOwner });
            }
        } catch (error) {
            dispatch(
                setBusinessSubscription({
                    businessId: business.id,
                    subscribed: Boolean(business.subscribed),
                }),
            );
        }
    };
}

export function shareBusinessAsync(
    business: BusinessModel,
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch) => {
        try {
            const sharingMethod = await businessService.shareBusiness(business);

            if (sharingMethod === SharingMethod.CLIPBOARD) {
                dispatch(showNotificationAsync('Скопировано в буфер обмена'));
            }
        } catch (error) {
            dispatch(showNotificationAsync('Не удалось поделиться данными'));
        }
    };
}

export function reportBusinessAsync(
    targetId: string,
    reason: ReportReasons,
): ThunkAction<Promise<void>, RootState, undefined, AnyAction> {
    return async (dispatch) => {
        try {
            await businessService.reportBusiness(targetId, reason);

            dispatch(addReportedBusiness(targetId));
            dispatch(showNotificationAsync('Мы будем показывать меньше таких публикаций.'));
        } catch (error) {
            dispatch(showNotificationAsync('Не удалось отправить жалобу на пост.'));
        }
    };
}
